import type { IssueState } from './index';

/**
 * Project Threads — long-running team discussions with the PM. A thread keeps
 * a PM-maintained running summary ("Where we are") and a ledger of the issues
 * the discussion discovered (`ThreadProposalDto`, suggested → filed/dismissed).
 *
 * The PM's turn protocol is BODY-EMBEDDED on purpose: the agent image's reply
 * parser forwards only `{ body }` from /aiai/chat-reply.json, so the summary
 * and proposals ride inside the body as fenced blocks (```thread-summary and
 * ```issue-proposal) that the API parses + strips with `parseThreadReplyBody`
 * before storing the message. No image rebuild is ever needed for this
 * protocol to work.
 */

/** Hard cap on the PM's running summary (Markdown). */
export const THREAD_SUMMARY_MAX_LENGTH = 2000;
/** Hard cap on a proposal title. */
export const THREAD_PROPOSAL_TITLE_MAX_LENGTH = 200;
/** Max tags per proposal; each tag is trimmed to THREAD_PROPOSAL_TAG_MAX_LENGTH. */
export const THREAD_PROPOSAL_TAGS_MAX = 5;
export const THREAD_PROPOSAL_TAG_MAX_LENGTH = 40;
/** Max proposals honoured per PM turn (extras are dropped). */
export const THREAD_PROPOSALS_PER_TURN_MAX = 10;
/** A new thread's title is seeded from its first post, trimmed to this. */
export const THREAD_TITLE_SEED_LENGTH = 60;
/** Hard cap on a human-set thread title. */
export const THREAD_TITLE_MAX_LENGTH = 200;

export type ThreadProposalStatus = 'suggested' | 'filed' | 'dismissed';
export const THREAD_PROPOSAL_STATUSES: readonly ThreadProposalStatus[] = ['suggested', 'filed', 'dismissed'];

/** One thread in the project's list (the detail adds the ledger). */
export interface ThreadDto {
  id: string;
  projectId: string;
  /** Human-editable; seeded from the first post. Null only on edge-case rows. */
  title: string | null;
  /** Email of the member who wrote the first post. Null when that author was deleted. */
  startedByEmail?: string | null;
  /** The PM's running "Where we are" (Markdown). Null until the PM's first summary. */
  summary: string | null;
  summaryUpdatedAt: string | null;
  /** Null = open. */
  resolvedAt: string | null;
  messageCount: number;
  /** Distinct human posters, plus one when the PM has posted. */
  participantCount: number;
  proposalCounts: { suggested: number; filed: number; dismissed: number };
  lastMessageAt: string;
  createdAt: string;
}

/** The issue a filed proposal became. */
export interface ThreadProposalIssueRef {
  id: string;
  code: string | null;
  title: string;
  state: IssueState;
}

/** One row of a thread's discovered-issues ledger. */
export interface ThreadProposalDto {
  id: string;
  threadId: string;
  /** The PM post that raised (or last refreshed) it; null if that post was deleted. */
  messageId: string | null;
  title: string;
  body: string;
  tags: string[];
  status: ThreadProposalStatus;
  /** Set once filed; null while suggested/dismissed, or if the issue was deleted. */
  issue: ThreadProposalIssueRef | null;
  decidedByEmail: string | null;
  decidedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ThreadDetailDto extends ThreadDto {
  proposals: ThreadProposalDto[];
}

/** One post in a thread (a human's or the PM's). */
export interface ThreadMessageDto {
  id: string;
  projectId: string;
  threadId: string;
  role: 'user' | 'pm';
  /** Set on user messages. */
  authorId?: string;
  authorEmail?: string;
  /** Set on pm messages: the chat-lane run that produced the reply. */
  runId?: string;
  body: string;
  createdAt: string;
}

/** A proposal as the PM writes it (one ```issue-proposal fence). */
export interface ThreadProposalEntry {
  title: string;
  body: string;
  tags: string[];
}

/** `parseThreadReplyBody` output: the storable body plus the extracted outputs. */
export interface ParsedThreadReply {
  /** The reply with every honoured fence removed. */
  body: string;
  /** The last ```thread-summary block, capped; null when none was present. */
  summary: string | null;
  /** Every valid ```issue-proposal block, in order, capped at THREAD_PROPOSALS_PER_TURN_MAX. */
  proposals: ThreadProposalEntry[];
}

/** Dedupe key for proposals within a thread: trimmed, lower-cased, whitespace-collapsed. */
export function normalizeProposalTitle(title: string): string {
  return title.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** A fenced block whose opening and closing fences sit on their own lines. */
function fenceRegex(lang: string): RegExp {
  return new RegExp(`^[ \\t]*\`\`\`${lang}[ \\t]*\\r?\\n([\\s\\S]*?)\\r?\\n[ \\t]*\`\`\`[ \\t]*$`, 'gm');
}

const SUMMARY_FENCE = fenceRegex('thread-summary');
const PROPOSAL_FENCE = fenceRegex('issue-proposal');

function parseProposalJson(raw: string): ThreadProposalEntry | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const o = parsed as { title?: unknown; body?: unknown; tags?: unknown };
  if (typeof o.title !== 'string' || typeof o.body !== 'string') return null;
  const title = o.title.trim().slice(0, THREAD_PROPOSAL_TITLE_MAX_LENGTH);
  const body = o.body.trim();
  if (!title || !body) return null;
  const tags = Array.isArray(o.tags)
    ? o.tags
        .filter((t): t is string => typeof t === 'string')
        .map((t) => t.trim().slice(0, THREAD_PROPOSAL_TAG_MAX_LENGTH))
        .filter(Boolean)
        .slice(0, THREAD_PROPOSAL_TAGS_MAX)
    : [];
  return { title, body, tags };
}

/**
 * Extract the PM's thread outputs from a reply body and strip them. The last
 * summary fence wins (capped at THREAD_SUMMARY_MAX_LENGTH); valid proposal
 * fences become entries (first THREAD_PROPOSALS_PER_TURN_MAX kept, the rest
 * dropped but still stripped); a malformed proposal fence is left in the body
 * untouched so the reader sees what the PM wrote. Runs of blank lines left
 * behind collapse to one.
 */
export function parseThreadReplyBody(body: string): ParsedThreadReply {
  let summary: string | null = null;
  let out = body.replace(SUMMARY_FENCE, (_m, inner: string) => {
    const text = inner.trim();
    if (text) summary = text.slice(0, THREAD_SUMMARY_MAX_LENGTH);
    return '';
  });
  const proposals: ThreadProposalEntry[] = [];
  out = out.replace(PROPOSAL_FENCE, (match, inner: string) => {
    const entry = parseProposalJson(inner);
    if (!entry) return match;
    if (proposals.length < THREAD_PROPOSALS_PER_TURN_MAX) proposals.push(entry);
    return '';
  });
  out = out.replace(/\n{3,}/g, '\n\n').trim();
  return { body: out, summary, proposals };
}
